import { Locale } from './types';

export const en: Locale = {
  hero: {
    desc: "Cracking on a bike 50km from home isn't heroic - it's just bad math. Instead of calling for a rescue, plan your ride smarter. Broom Wagon turns your GPX track and watts into a precise fueling plan. Finish every ride on your own terms.",
    tagline: 'Cycling engineering for the adventure. So your day ends at sunset, not with a power cut on the road.',
    free: 'Completely free. Because we know that watts and gear already cost enough.',
  },
  motto: 'Broom Wagon? Not this time!',
  tabs: {
    walkthrough: 'How it works',
    ecosystem: 'On the road',
    science: 'The Science',
    broomWagon: 'The Lore',
  },
  lore: {
    heading: 'What exactly is a Broom Wagon?',
    paragraphs: [
      'The broom wagon (French: voiture or camion balai) has dragged itself at the back of the peloton for over a century. Its job? To literally sweep up riders who have abandoned or cracked on a climb.',
      'In the amateur world, this legendary vehicle takes much worse forms: a begging phone call to your spouse, an absurdly expensive taxi, or dragging your cleats onto a local train.',
      "Our app is your insurance policy. Eat to the plan, hold your watts, and let the broom wagon pick up someone else. <a href=\"https://www.velominati.com/commentary/the-rules/#5\" target=\"_blank\" rel=\"noreferrer\" style=\"color: #c4cdd5; text-decoration: underline; text-underline-offset: 2px;\" onmouseover=\"this.style.color='#fff'\" onmouseout=\"this.style.color='#c4cdd5'\">Rule #5</a> still applies, but starving on a bike is nothing to be proud of.",
    ],
    linksLabel: 'READ MORE ABOUT THE HISTORY:',
  },
  storyItems: [
    {
      label: 'Drop your route',
      desc: 'Upload your GPX file. We’ll do the math, reading every gradient to build a real-world physics model of your suffering.',
      alt: 'GPX upload',
    },
    {
      label: 'Calibrate the engine',
      desc: 'Weight, FTP, and vibe: from a Sunday coffee ride to an all-out effort. We’ll calculate exactly how many carbs you’re going to burn.',
      alt: 'Strategy options',
    },
    {
      label: 'Plan your feed zones',
      desc: 'We generate optimal eating windows on your route. Drag and drop them to match that crucial gas station stop.',
      alt: 'Fuel points editor',
    },
    {
      label: 'Stuff your pockets',
      desc: 'Pick your favorite gels and bars. We distribute them to prevent flavor fatigue and hit your exact targets.',
      alt: 'Products selection',
    },
    {
      label: 'Cancel the bonk',
      desc: 'Get a km-by-km schedule. No more guessing. Know exactly what to eat and when to stay ahead of the Broom Wagon.',
      alt: 'Fuel plan results',
    },
  ],
  why: [
    {
      title: 'This is not a dumb 60g/h spreadsheet',
      desc: "Your body doesn't care about averages. A 10% gradient at 300W isn't the same as drafting in zone 2. Our engine turns rolling resistance, aero, and gravity into pure carbs. Take risks in the breakaway, not in the feed zone.",
    },
    {
      title: 'Rule #X: Eating is not cheating',
      desc: "Starving yourself on a bike isn't heroic - it's just bad math. A precise plan lets you hold your watts in the fourth hour instead of slowly fading away. Suffer because you're riding on the limit, not because you forgot to eat.",
    },
    {
      title: 'Bonk Radar',
      desc: 'The algorithm tracks your energy balance kilometre by kilometre. If the math says a climb will pull your plug, Broom Wagon lights up a red flag before you even leave the house.',
    },
    {
      title: 'Physiology without a PhD',
      desc: 'Intestinal absorption ceilings (SGLT1/GLUT5), power-zone carbohydrate oxidation, and glycogen depletion. All the academic boredom is tucked under the hood so you just get simple commands: what to put in your mouth and when.',
    },
  ],
  sticker: {
    desc: 'Clean cockpit, clear mind. Generate a top tube sticker with your schedule and elevation profile. Your head unit should only display what matters: Speed, Distance, and Suffering.',
  },
  integrations: {
    intro: "The top tube sticker is your freedom from electronics - a tangible strategy that doesn't need a battery. If you prefer a digital cockpit, we're building direct sync for Garmin, Wahoo, and an app with alerts.",
    comingSoon: 'COMING SOON',
    comingSoonDesc: "Broom Wagon is a 100% independent project, and hardware integrations are our next big goal. If you want to show us that the tool works on your routes - drop us a virtual coffee or send us a feature request. It's the best signal we can get that we're riding in the right direction.",
    items: [],
  },
  science: {
    title: 'Under the hood',
    intro: 'Broom Wagon is built on established models from exercise physiology and cycling biomechanics - not heuristics or rule-of-thumb tables.',
    sections: [
      {
        title: 'Power Model',
        desc: 'We compute mechanical power output for every GPX segment. Gravity, wind, and rolling resistance vs. your legs. The result? A watt-by-watt route profile straight out of pro analysis software.',
      },
      {
        title: 'Zone-based Carbs',
        desc: "Spinning in zone 2? You're mostly burning fat. Pushing past threshold (Z4+)? Carbs become jet fuel. The app maps your route to Coggan's zones and cranks up the fueling targets on the climbs.",
      },
      {
        title: 'Gut Limits',
        desc: "Straight glucose bottlenecks in your gut at ~60 g/h (SGLT1 transporter). Mix in fructose (GLUT5) and you're pushing 90 g/h. We enforce these physiological speed limits so you don't end up with GI distress in a ditch.",
      },
      {
        title: 'Emptying the Tank',
        desc: "Your glycogen stores are only about 400-600 grams. Broom Wagon constantly checks what you're burning against what you're eating, warning you about an imminent bonk long before the crisis hits.",
      },
    ],
    referencesLabel: 'REFERENCES',
    statsLabel: 'Key numbers',
    stats: [
      { value: '60 g/h',    label: 'Standard protocol',   sub: 'Good for a coffee ride. Not enough for the Alps.',               tooltip: 'The upper limit of glucose absorption alone. A safe dose for most cyclists that requires no gut training - but it may not be enough on race day.' },
      { value: '90 g/h',    label: 'Training the gut',    sub: 'Your intestines need to learn to accept as much fuel as your engine burns.', tooltip: 'Requires a glucose-fructose mix (e.g. 1:0.8). Above 60 g/h your body needs multiple fuel transporters. Train your gut during long rides to avoid GI distress on race day.' },
      { value: '400-600 g', label: 'Your capital',        sub: 'When it hits zero, your only drive will be willpower and Broom Wagon.',      tooltip: 'Glycogen stored in your legs and liver. It\'s your battery that you systematically drain. When it hits zero the Broom Wagon is waiting - unless you top up your carbs in time.' },
      { value: 'Z4+',       label: 'Point of no return',  sub: 'You burn carbs faster than you can swallow them. Plan wisely.',             tooltip: 'Above the lactate threshold your body almost completely abandons fat burning in favour of glycogen. In this zone every minute without eating brings you closer to the bonk.' },
    ],
    sciencePromo: "Your body doesn't care about averages. Our engine turns rolling resistance, aero, and gravity into pure carbs. Take risks in the breakaway, not in the feed zone.",
    loginRequired: 'Sign in to access the full physics and physiological model documentation.',
  },
  signin: {
    prompt: 'Sign in or create a free account to get started.',
  },
  nav: {
    planner: 'Planner',
    history: 'History (Coming Soon)',
    products: 'Products (Coming Soon)',
    settings: 'Settings',
    account: 'Account',
    manual: 'User Manual',
    manualComingSoon: 'The manual is being prepared. Check back soon.',
    about: 'About us',
    support: 'Support us',
    logout: 'Logout',
  },
  support: {
    heading: 'Support the project',
    sections: [
      {
        title: 'Who’s behind this',
        desc: 'We’re the team checking wind direction, tire pressure, and VAM before a ride. We have an incurable data obsession, but the bike always comes first. Built by cyclists, for cyclists, to stop the bonk spiral.',
      },
      {
        title: 'What is and stays free',
        desc: 'GPX upload, energy model, fueling planner, bonk detection, and sticker generation - all free, and that won’t change.',
      },
      {
        title: 'Watts aren\'t free',
        desc: 'We operate independently, giving us the freedom to build the best tool for cyclists. Your support helps us maintain that pace and cover infrastructure costs to keep Broom Wagon moving forward.',
      },
    ],
    actionsLabel: 'How you can help',
    actions: [
      { label: 'Buy us a coffee', desc: 'One-time support via Buy Me a Coffee. Every coffee is hours of development.' },
      { label: 'Spread the word', desc: 'Tell other cyclists about Broom Wagon. A recommendation is hugely valuable.' },
      { label: 'Send feedback', desc: 'Ideas, bugs, thoughts - every message helps us build a better product.' },
    ],
    shareTweet: 'How do you plan your fueling for long rides? Found an app that turns watts and GPX into carb targets. Looks interesting.',
    linksLabel: 'SUPPORT THE PROJECT:',
  },
  about: {
    heading: 'Broom Wagon',
    desc: 'An independent project built from a passion for data and a shared hatred of the bonk. We want you to focus on the views and the burn, not the math and an empty tank.',
    contactLabel: 'Contact',
    email: 'contact@broomwagon.app',
    followLabel: 'Follow us',
  },
  settings: {
    language: {
      heading: 'Language',
    },
    cyclist: {
      heading: 'Cyclist Profile',
      ftp: 'FTP (watts)',
      ftpPlaceholder: 'Your functional threshold power',
      weight: 'Weight (kg)',
      weightPlaceholder: 'Your body weight',
      maxAbsorption: 'Carbohydrate Tolerance (g/h)',
      gelsPerHourHint: '≈ {count} gels per hour (25g/gel)',
    },
    bike: {
      heading: 'Bike',
      weight: 'Bike Weight (kg)',
    },
    save: 'Save Settings',
    saving: 'Saving...',
    saved: 'Settings saved',
  },
  planner: {
    strategyOptions: 'Strategy Options',
    strategyGroups: {
      intensity: 'Intensity and Time',
      fuelModel: 'Fuel Model',
    },
    targetZone: 'Target Zone',
    frequency: 'Eat every (min):',
    frequencyHint: 'Set how often you plan to fuel up.',
    safetyMargin: 'Safety Margin (Overfueling)',
    safetyMarginDescriptions: [
      { min: 0, max: 5, text: 'Going with the flow. Optimal absorption.' },
      { min: 6, max: 15, text: 'Aggressive loading. You risk mild discomfort, but you\'re avoiding the bonk.' },
      { min: 16, max: 100, text: 'Danger zone. Make sure your gut can handle it.' },
    ],
    proModeWarning: 'You\'re in Pro mode. Skip the fructose training and you\'ll be bonking in the bushes.',
    cyclistProfile: 'Cyclist Profile',
    edit: 'Edit',
    close: 'Close',
    back: 'Back',
    nextStep: 'Next Step',
    cyclist: {
      ftp: 'FTP',
      weight: 'Weight',
      bike: 'Bike',
    },
    steps: {
      routeAnalysis: 'Route Analysis',
      strategyOptions: 'Strategy Options',
      fuelPoints: 'Fuel Points',
      products: 'Products',
      fuelPlan: 'Fuel Plan',
      summary: 'Summary',
    },
    gpx: {
      uploadPrompt: 'Drop a .gpx file here or click to browse',
      analyzing: 'Analyzing...',
      distance: 'Distance',
      elevationGain: 'Elevation Gain',
      elevationLoss: 'Elevation Loss',
      segments: 'Segments',
      chartLabel: 'Distance (km)',
      elevationLabel: 'Elevation',
    },
    chartHint: 'Click to add',
    products: {
      name: 'Name',
      carbs: 'Carbs',
      type: 'Type',
      qty: 'Qty',
      minPortion: 'Min Portion',
      whole: 'whole',
      carbsUnit: 'Carbs (g)',
      qtyUnit: 'Qty',
      minPortionUnit: 'Min Portion (g)',
      save: 'Save',
      cancel: 'Cancel',
      verified: 'Verified by partner',
      edit: 'Edit product',
      delete: 'Delete product',
      partnerStore: 'Go to partner store',
      placeholderName: 'Name',
      resetDefaults: 'Reset to defaults',
      addProduct: '+ Add Product',
      partnerModalText: 'This product comes from a trusted partner. You can visit their store to purchase',
      partnerModalComingSoon: 'Soon you will be able to go directly to the partner store.',
    },
    summary: {
      route: 'Route',
      distance: 'Distance',
      duration: 'Duration',
      elevationGain: 'Elevation Gain',
      elevationLoss: 'Elevation Loss',
      fuelPoints: 'Fuel Points',
      packingList: 'Packing List',
      cyclistProfile: 'Cyclist Profile',
      ftp: 'FTP',
      weight: 'Weight',
      zone: 'Zone',
      strategy: 'Strategy',
      refuelEvery: 'Refuel Every',
      maxAbsorption: 'Max Absorption',
      maxSurplus: 'Max Surplus',
      maxDeficit: 'Max Deficit',
      generateSticker: 'Generate Sticker',
      stickerTitle: 'Fuel Plan Sticker',
      downloadSvg: 'Download SVG',
    },
    stats: {
      carbs: 'Carbs',
      balance: 'Balance',
      average: 'Average',
      burned: 'burned',
    },
  },
  mobileFootnote: 'Watt analysis needs space. Planner optimized for large screens. Mobile companion coming soon.',
  langToggle: 'PL',
  footerHeading: 'The Grupetto',
};
